import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { Broadcast } from '../../../models/Broadcast';
import { BroadcastFilter } from '../../../models/BroadcastFilter';
import { CANCELLED, PENDING, SENDING, SENT, SENT_WITH_ERRORS, SANITIZING, SCHEDULED } from '../../../models/BroadcastStatus';
import { PaginatedResult } from '../../../shared/model/paginated-result';
import { BroadcastStatusPipe } from '../../../shared/pipes/BroadcastStatus.pipe';
import { IntegrationBroadcastsService } from '../../../shared/services/integration-broadcasts.service';
import { formatDateParam, isInvalidPeriod, parseDateParam } from '../date-param';

/**
 * O rótulo do filtro vem do MESMO pipe que a coluna "Status" da tabela usa.
 *
 * Escrever os nomes à mão aqui já divergiu uma vez: o filtro dizia "Aguardando
 * revisão" e "Enviado com erros" enquanto a linha correspondente exibia
 * "Pendente Análise" e "Enviado com alguns erros" — o operador filtra por um
 * texto e recebe linhas escritas com outro, o que se lê como filtro errado.
 * Derivando do pipe, as duas fontes não têm como se separar de novo.
 */
const statusLabel = new BroadcastStatusPipe();

/**
 * "SMS via Integração", level 3 — every message sent by one automation.
 *
 * This is the screen an operator lands on to answer "did this contact get the
 * SMS?", so the filters are the ones that question needs: phone number, message
 * text, status and period. Each row is one SMS, since the platform calls the API
 * once per contact.
 *
 * Reached as /integration-sms/view/:source/sends?automation=<name>. The
 * automation travels as a query param because it is free text typed on the
 * platform side; an empty value selects the sends that arrived with no
 * automation declared, which is a real group and not "no filter".
 */
@Component({
  selector: 'app-integration-sms-sends',
  templateUrl: './integration-sms-sends.component.html',
  styleUrls: ['./integration-sms-sends.component.scss']
})
export class IntegrationSmsSendsComponent implements OnInit, OnDestroy {
  source: string;
  automation: string | null = null;

  phone = '';
  text = '';
  status = '';
  startedMonth: Date;
  endedMonth: Date;

  page = 1;
  pageSize = 15;
  loading: boolean;
  result: PaginatedResult<Broadcast[]>;
  subscriptions: Subscription[] = [];

  /**
   * Os estados que um SMS de integração REALMENTE alcança.
   *
   * `SENT_WITH_ERRORS` e `PENDING` faltavam, e são os dois que mais importam:
   * o primeiro é o resultado de um envio que falhou em parte — justamente o que
   * o operador vem procurar nesta tela —, e o segundo é onde o disparo para
   * quando a conta tem revisão de conteúdo ligada sem estar habilitada à
   * integração. Sem eles no filtro, essas linhas eram inalcançáveis.
   *
   * `IMPORTING` fica de fora de propósito: ele só é setado no caminho do painel
   * (BroadcastService::createOrUpdate), nunca pelo PontaltechAdapter, então um
   * disparo de integração jamais assume esse estado. Oferecê-lo seria um filtro
   * que devolve vazio sempre.
   */
  readonly statuses = [
    SENT,
    SENT_WITH_ERRORS,
    SENDING,
    SCHEDULED,
    PENDING,
    SANITIZING,
    CANCELLED,
  ].map(id => ({ id, name: statusLabel.transform(id) }));

  constructor(
    private integrationBroadcastsService: IntegrationBroadcastsService,
    private route: ActivatedRoute,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.subscriptions.push(
      this.route.params.subscribe(params => this.source = params.source),

      this.route.queryParams.subscribe(params => {
        // '' is meaningful (undeclared automation); undefined means "all".
        this.automation = params.automation !== undefined ? params.automation : null;
        this.page = +params.page || 1;
        this.phone = params.phone || '';
        this.text = params.text || '';
        this.status = params.status || '';
        this.startedMonth = parseDateParam(params.start_date);
        this.endedMonth = parseDateParam(params.end_date);
        this.list();
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  get automationLabel(): string {
    if (this.automation === null) {
      return 'Todas as automações';
    }

    return this.automation === '' ? 'Sem automação identificada' : this.automation;
  }

  get invalidPeriod(): boolean {
    return isInvalidPeriod(this.startedMonth, this.endedMonth);
  }

  list(): void {
    // Aqui a guarda protege DUAS entradas, não só o Enter no form: nesta tela o
    // período também chega pela URL (`start_date`/`end_date`), então um link
    // compartilhado com o intervalo invertido dispararia a busca sem passar por
    // botão nenhum.
    if (this.invalidPeriod) {
      return;
    }

    const filter: BroadcastFilter = {
      startDate: formatDateParam(this.startedMonth),
      endDate: formatDateParam(this.endedMonth),
      text: this.text,
      order: null,
      orderDirection: null,
      author: null
    };

    this.loading = true;

    this.subscriptions.push(
      this.integrationBroadcastsService
        .listSends(this.source, this.automation, this.page, this.pageSize, filter, this.phone, this.status)
        .pipe(finalize(() => this.loading = false))
        .subscribe(result => this.result = result)
    );
  }

  /**
   * Pushes the filters into the URL so a filtered view is shareable.
   *
   * Nenhum filtro pode ficar de fora daqui. A busca só acontece porque a
   * navegação muda a URL: o app roda com `useHash` e `onSameUrlNavigation` no
   * default ('ignore'), então navegar para uma URL idêntica é no-op e o
   * `list()` — que roda dentro do subscribe de `queryParams` — nunca dispara.
   * Um filtro que não chega até aqui simplesmente para de funcionar da segunda
   * busca em diante.
   */
  search(page: number = 1): void {
    const queryParams: any = { page };

    if (this.automation !== null) {
      queryParams.automation = this.automation;
    }

    if (this.phone) {
      queryParams.phone = this.phone;
    }

    if (this.text) {
      queryParams.text = this.text;
    }

    if (this.status) {
      queryParams.status = this.status;
    }

    if (this.startedMonth) {
      queryParams.start_date = formatDateParam(this.startedMonth);
    }

    if (this.endedMonth) {
      queryParams.end_date = formatDateParam(this.endedMonth);
    }

    this.router.navigate(['/integration-sms/view', this.source, 'sends'], { queryParams });
  }

  clearFilters(): void {
    this.phone = '';
    this.text = '';
    this.status = '';
    this.startedMonth = null;
    this.endedMonth = null;
    this.search(1);
  }

  phoneOf(send: Broadcast): string {
    return send.contacts?.length ? send.contacts[0].phone : '-';
  }

  sentDateOf(send: Broadcast): Date | string {
    return send.contacts?.length && send.contacts[0].sent_date
      ? send.contacts[0].sent_date
      : send.scheduled_date;
  }
}
